import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useInquiry } from '../../context/InquiryContext';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { canSendInquiry } from '../../utils/membershipUtils';
import UpgradePromptModal from '../UpgradePromptModal/UpgradePromptModal';
import * as messageService from '../../services/messageService';
import './InquirySidebar.css';
import { LOGO_PLACEHOLDER } from '../../utils/placeholders';

const InquirySidebar = ({ onLoginRequired }) => {
  const { formatPriceRange } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { planName } = useSubscription();
  const { 
    inquiryItems, 
    isInquiryOpen, 
    closeInquiry, 
    removeFromInquiry, 
    updateQuantity,
    clearInquiry,
    getItemsBySeller 
  } = useInquiry();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleSendInquiry = async () => {
    console.log('Send Inquiry button clicked!');
    console.log('Inquiry items:', inquiryItems);
    console.log('User:', user);
    
    if (inquiryItems.length === 0) {
      alert('Please add products to your inquiry first.');
      return;
    }

    // Check if user is logged in
    if (!user) {
      console.log('User not logged in, triggering login modal');
      // Trigger login modal
      if (onLoginRequired) {
        onLoginRequired();
      } else {
        alert('Please login to send an inquiry.');
      }
      return;
    }

    // Product inquiries are allowed for all authenticated users regardless of plan

    // Check if items have sellerId
    const itemsWithoutSeller = inquiryItems.filter(item => !item.sellerId);
    if (itemsWithoutSeller.length > 0) {
      console.warn('Some items missing sellerId:', itemsWithoutSeller);
      alert('Some items are missing seller information. Please clear your inquiry and add products again.');
      return;
    }

    const buyerId = user.id;
    console.log('Sending inquiry as user:', buyerId, 'with', inquiryItems.length, 'items');

    try {
      setSending(true);
      
      // Send inquiry via API
      const result = await messageService.sendInquiry(buyerId, inquiryItems, message);
      console.log('Inquiry sent successfully:', result);
      
      const inboxPath = location.pathname.startsWith('/mobile') ? '/mobile/messenger' : '/messages';
      navigate(isAdmin ? '/admin/messages' : inboxPath);
      clearInquiry();
      closeInquiry();
      setMessage('');
    } catch (error) {
      console.error('Error sending inquiry:', error);
      alert(`Failed to send inquiry: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  if (!isInquiryOpen) return null;

  const itemsBySeller = getItemsBySeller();

  return (
    <>
      <div className="inquiry-overlay" onClick={closeInquiry} />
      <div className="inquiry-sidebar">
        <div className="inquiry-header">
          <h2>Your Inquiry</h2>
          <button className="close-btn" onClick={closeInquiry}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="inquiry-content">
          {inquiryItems.length === 0 ? (
            <div className="empty-inquiry">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.707 15.293C4.077 15.923 4.523 17 5.414 17H17M17 17C15.895 17 15 17.895 15 19C15 20.105 15.895 21 17 21C18.105 21 19 20.105 19 19C19 17.895 18.105 17 17 17ZM9 19C9 20.105 8.105 21 7 21C5.895 21 5 20.105 5 19C5 17.895 5.895 17 7 17C8.105 17 9 17.895 9 19Z" stroke="#DADADA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>No products in inquiry</p>
              <span>Click "Inquire Now" on products to add them</span>
            </div>
          ) : (
            <>
              {itemsBySeller.map((seller) => (
                <div key={seller.sellerId} className="seller-group">
                  <div className="seller-info">
                    <img 
                      src={seller.sellerAvatar || LOGO_PLACEHOLDER} 
                      alt={seller.sellerName}
                      className="seller-avatar"
                    />
                    <span className="seller-name">{seller.sellerName}</span>
                  </div>
                  <div className="seller-products">
                    {seller.items.map((item) => (
                      <div key={item.id} className="inquiry-item">
                        <img 
                          src={item.image} 
                          alt={item.description}
                          className="item-image"
                        />
                        <div className="item-details">
                          <p className="item-description">{item.description}</p>
                          <p className="item-price">
                            {formatPriceRange(item.priceMin, item.priceMax)}
                            <span className="price-per">/ {item.pricePer}</span>
                          </p>
                          <div className="quantity-row">
                            <label>Qty:</label>
                            <input 
                              type="number" 
                              min="1" 
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                              className="quantity-input"
                            />
                            <span className="unit">{item.pricePer}</span>
                          </div>
                        </div>
                        <button 
                          className="remove-btn"
                          onClick={() => removeFromInquiry(item.id)}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#ff4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="inquiry-message">
                <label>Add a message (optional)</label>
                <textarea 
                  placeholder="Type your message to the seller..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {inquiryItems.length > 0 && (
          <div className="inquiry-footer">
            <div className="inquiry-summary">
              <span>{inquiryItems.length} product(s)</span>
              <button className="clear-btn" onClick={clearInquiry}>Clear All</button>
            </div>
            <button 
              className="send-inquiry-btn" 
              onClick={handleSendInquiry}
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send Inquiry'}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Send Inquiry"
        message="Basic plan users cannot send inquiries via messaging. Please upgrade to GCC Business or Global Enterprise plan to message suppliers."
      />
    </>
  );
};

export default InquirySidebar;
